import { useState } from "react";
import type { LucideIcon } from "lucide-react";
import { Boxes, FileText, Percent, PiggyBank, ReceiptText } from "lucide-react";
import type { EmployeeActivityDetail } from "@/lib/reporting.api";

export interface RecentActivityEvent {
  id: string;
  icon: LucideIcon;
  label: string;
  detail: string;
  amount: number;
  date: string;
}

function fmtDate(d: string): string {
  return new Date(d).toLocaleDateString("fr-FR");
}

function money(n: number): string {
  return `${n.toLocaleString("fr-FR")} XAF`;
}

// Merges the already-loaded activity datasets into one chronological feed —
// no backend change, per prompt.md's "Activité récente" requirement. Épargne
// deposit+withdraw same-day/same-amount pairs are the inscription-fee
// pattern used across the app (see PartyDetailPage's isInscriptionFeePairLeg)
// — this flat activity list has no accountId/inscriptionFeeAmount to match
// against, so the merge here is intentionally conservative: only collapses
// when a given (date, amount) group is exactly one deposit + one withdraw,
// never a larger or lopsided group, to avoid mislabeling a real transaction.
export function buildRecentEvents(detail: EmployeeActivityDetail, cap = 30): RecentActivityEvent[] {
  const byDateAmount = new Map<string, typeof detail.savingsTransactions>();
  for (const row of detail.savingsTransactions) {
    const key = `${row.date}|${row.amount}`;
    const bucket = byDateAmount.get(key) ?? [];
    bucket.push(row);
    byDateAmount.set(key, bucket);
  }

  const savingsEvents: RecentActivityEvent[] = [];
  const handledKeys = new Set<string>();
  for (const row of detail.savingsTransactions) {
    const key = `${row.date}|${row.amount}`;
    if (handledKeys.has(key)) continue;
    const bucket = byDateAmount.get(key)!;
    const isFeePair =
      bucket.length === 2 &&
      bucket.some((r) => r.nature === "deposit") &&
      bucket.some((r) => r.nature === "withdraw");

    if (isFeePair) {
      handledKeys.add(key);
      savingsEvents.push({
        id: `savings-pair-${key}`,
        icon: PiggyBank,
        label: "Frais d'inscription épargne",
        detail: "Épargne voyage",
        amount: 0,
        date: row.date,
      });
    } else {
      savingsEvents.push({
        id: `savings-${row.id}`,
        icon: PiggyBank,
        label: row.nature === "deposit" ? "Dépôt épargne" : "Retrait épargne",
        detail: "Épargne voyage",
        amount: row.amount,
        date: row.date,
      });
    }
  }

  const events: RecentActivityEvent[] = [
    ...detail.invoices.map((row) => ({
      id: `invoice-${row.id}`,
      icon: FileText,
      label: "Facture émise",
      detail: `${row.number ?? `#${row.id}`} · ${row.partyName}`,
      amount: row.amount,
      date: row.date,
    })),
    ...detail.payments.map((row) => ({
      id: `payment-${row.id}`,
      icon: ReceiptText,
      label: "Paiement reçu",
      detail: row.invoiceNumber ?? `#${row.invoiceId}`,
      amount: row.amount,
      date: row.date,
    })),
    ...detail.commissions.map((row) => ({
      id: `commission-${row.id}`,
      icon: Percent,
      label: "Commission enregistrée",
      detail: row.typeLabel,
      amount: row.amount,
      date: row.date,
    })),
    ...detail.stockMovements.map((row) => ({
      id: `stock-${row.id}`,
      icon: Boxes,
      label: "Mouvement de stock",
      detail: row.articleName,
      amount: 0,
      date: row.date,
    })),
    ...savingsEvents,
  ];

  return events.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()).slice(0, cap);
}

const VISIBLE_DEFAULT = 5;

export function EmployeeRecentActivity({ events }: { events: RecentActivityEvent[] }) {
  const [expanded, setExpanded] = useState(false);
  const visible = expanded ? events : events.slice(0, VISIBLE_DEFAULT);

  return (
    <div className="bg-card border border-border rounded-lg px-4 py-3 h-full flex flex-col">
      <p className="text-[12px] font-semibold text-foreground border-b border-border pb-2 mb-3">
        Activité récente
      </p>
      {events.length === 0 ? (
        <p className="py-4 text-center text-[12px] text-muted-foreground">
          Aucune activité sur cette période.
        </p>
      ) : (
        <div className="grid gap-2">
          {visible.map((event) => {
            const Icon = event.icon;
            return (
              <div
                key={event.id}
                className="grid grid-cols-[34px_1fr_auto] items-center gap-3 rounded-lg border border-border/70 bg-surface-muted/40 px-3 py-2"
              >
                <span className="flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-lg bg-brand-gold-light/30 text-brand-gold-dark">
                  <Icon size={15} />
                </span>
                <div className="min-w-0">
                  <p className="truncate text-[12.5px] font-medium text-body">{event.label}</p>
                  <p className="truncate text-[11px] text-muted-foreground">
                    {event.detail} · {fmtDate(event.date)}
                  </p>
                </div>
                {event.amount > 0 && (
                  <span className="text-[12px] font-semibold text-body tabular-nums">
                    {money(event.amount)}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      )}
      {events.length > VISIBLE_DEFAULT && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="mt-3 text-[11.5px] font-semibold text-brand-gold-dark hover:underline"
        >
          {expanded ? "Réduire" : `Voir tout (${events.length})`}
        </button>
      )}
    </div>
  );
}
